// Copyright 2025, University of Maryland, All Rights Reserved
interface Concept {
  concept_name: string;
  difficulty_level: number;
  problems_using_concept?: string[];
}

interface ConceptsCoveredProps {
  concepts: Concept[];
  documentType?: string;
}

export function ConceptsCovered({ concepts, documentType }: ConceptsCoveredProps) {
  if (!concepts || concepts.length === 0) {
    return null;
  }

  // Check if this is a syllabus document type
  const isSyllabus = documentType?.toLowerCase().includes('syllabus');
  
  // Don't show concepts section for syllabi
  if (isSyllabus) {
    return null;
  }

  const getDifficultyColor = (level: number) => {
    if (level >= 8) return 'bg-[#E21833] text-white';
    if (level >= 6) return 'bg-red-100 text-[#B91428]';
    if (level >= 4) return 'bg-gray-200 text-gray-800';
    return 'bg-[#FFD200] text-gray-900';
  };

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg pdf-page-break-avoid">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <span className="text-3xl mr-3">🧠</span>
        Concepts ({concepts.length})
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {concepts.map((concept, index) => (
          <div key={index} className="bg-white p-4 rounded-lg border border-gray-200  transition-colors">
            <h3 className="font-semibold text-gray-800 mb-2">{concept.concept_name}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Difficulty:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(concept.difficulty_level)}`}>
                  {concept.difficulty_level}/10
                </span>
              </div>
              <div>
                <span className="text-gray-600">Problems:</span>
                <span className="ml-2 text-gray-800">{concept.problems_using_concept?.join(', ') || 'N/A'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
